import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment-timezone';
import {
  Box,
  Typography,
  ListItemButton,
  Avatar,
  Icon,
  Grid,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import useQuery from 'hooks/useQuery';
import LinkedProviderPopover from 'components/MenuNew/LinkedProviderPopover';
import { AvailabilityControl } from 'components/Availability';
import { getAvailabilityStatus, renderPermanently } from 'utils/soldOut';
import { toSoldOutStatus } from 'constants/availabilityStatus';
import InfoBulkIcon from 'assets/icons/info_icon_bulk.svg';
import { showPriceRange } from 'utils/menu';
import {
  checkLinkedProviderWebsite,
  linkedWithProvider,
} from 'helpers/linkedWithProvider';
import { t } from 'i18next';
import useProviderConfig from 'hooks/useProviderConfig';
import { SET_PRODUCT_EVENTS_NEXT_LOCATION } from 'components/ConfirmProductActions/constants';
import { useDispatch, useSelector } from 'react-redux';
import { patchProductSoldOutById } from 'redux/actions/Menu/products';

export default function ProductItemVirtual({ product, menu, cid, category }) {
  const { _id } = product || {};
  const dispatch = useDispatch();
  const { providers } = useProviderConfig();
  const history = useHistory();
  const query = useQuery();
  const productId = query.get('pid');
  const { currentBusiness } = useSelector(({ business }) => business);

  const handleAvailabilityChange = (availabilityStatus) => {
    if (!_id) return;
    dispatch(
      patchProductSoldOutById(_id, {
        status: toSoldOutStatus(availabilityStatus),
      })
    );
  };

  const linked = useMemo(() => {
    return (
      menu.connectedProviders &&
      linkedWithProvider(menu.connectedProviders, providers)
    );
  }, [menu]);

  const checkWebsite = useMemo(() => {
    return (
      menu.connectedProviders &&
      checkLinkedProviderWebsite(menu.connectedProviders, providers)
    );
  }, [menu]);

  if (!product) {
    return null;
  }

  const handleOpenProduct = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    const search = `?entityType=item&pid=${product._id}&cid=${cid}&mode=edit`;
    dispatch({
      type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
      payload: `${history.location.pathname}${search}`,
    });
    history.push({
      search: search,
    });
  };

  return (
    <Box sx={{ width: '100%', pl: 3 }} className="nav-item-draggable" key={_id}>
      <ListItemButton
        sx={{
          background: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          py: 2,
        }}
        key={product._id}
        id={
          !product?.bulkInfo?.isSaved &&
          productId === _id &&
          linked &&
          checkWebsite
            ? 'overview-selected'
            : ''
        }
        className={
          !product?.bulkInfo?.isSaved && linked && checkWebsite
            ? 'overview-items'
            : ''
        }
        onClick={(e) => handleOpenProduct(e, product)}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'space-between', width: '100%' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 1 }}>
              <LinkedProviderPopover
                id={_id}
                categoryId={category._id}
                connectedProviders={product.connectedProviders}
                targetConnectedProviders={category.connectedProviders}
                showCount={false}
                target="item"
                overrideStyle={{
                  m: 0,
                }}
                targetData={product}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            {product?.image?.url ? (
              <Avatar
                sx={{
                  bgColor: '#E2E2E2',
                  width: '48px',
                  height: '48px',
                  ml: 0,
                }}
                alt={product.name}
                src={`${product?.image?.url}?tr=w-48,h-48`}
              ></Avatar>
            ) : (
              <Avatar
                sx={{
                  bgColor: '#E2E2E2',
                  width: '48px',
                  height: '48px',
                  ml: 0,
                }}
              >
                <Icon
                  sx={{
                    color: '#323232',
                  }}
                  component={ImageOutlinedIcon}
                />
              </Avatar>
            )}

            <Box sx={{ width: '100%' }}>
              <Grid container>
                <Grid item>
                  <Box
                    sx={{
                      display: 'flex',
                      ml: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#141414',
                        fontWeight: 500,
                      }}
                      variant="body1"
                    >
                      {product.name || 'Name'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      ml: 2,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#434343',
                          fontWeight: 400,
                        }}
                        variant="caption"
                      >
                        {product.description || null}
                      </Typography>
                      {renderPermanently(product, moment) ? (
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#EF3061',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            mt: 0.5,
                            whiteSpace: 'nowrap',
                          }}
                          variant="body1"
                        >
                          <Box
                            component="img"
                            sx={{ mr: 0.5 }}
                            src={InfoBulkIcon}
                            alt="INFO_BULK_ICON"
                          />
                          {t(renderPermanently(product, moment))}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: '#141414',
                        fontWeight: 500,
                        flexShrink: 0,
                        minWidth: 56,
                        textAlign: 'right',
                      }}
                      variant="caption"
                    >
                      {showPriceRange(product)}
                    </Typography>
                    <AvailabilityControl
                      value={getAvailabilityStatus(product, currentBusiness)}
                      onChange={handleAvailabilityChange}
                      entityName={product.name}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </ListItemButton>
    </Box>
  );
}
